import { circleDiameterPoints } from '../shared/circle-geometry.js';
import type { EditorRuntimeReadyState } from './editor-state.js';
import { latLngToScreen, screenPointToLatLng } from './map-math.js';

export interface ExactRadiusPlacement {
  radiusKm: number | null;
  unit: string;
  mapBottom: number | null;
}

export function exactRadiusPreviewPoint(state: EditorRuntimeReadyState) {
  const pending = state.pendingExactRadiusCircle;
  if (!pending) return null;
  if (pending.mapBottom === null && state.cursorPreviewPoint) return state.cursorPreviewPoint;
  const bounds = state.mapElement.getBoundingClientRect();
  const top = document.getElementById('mobile-top-controls')?.getBoundingClientRect().bottom ?? bounds.top;
  const bottom = pending.mapBottom ?? bounds.bottom;
  return { x: bounds.width / 2, y: (Math.max(bounds.top, top) + bottom) / 2 - bounds.top };
}

export function drawExactRadiusPreview(state: EditorRuntimeReadyState) {
  const pending = state.pendingExactRadiusCircle;
  const point = exactRadiusPreviewPoint(state);
  if (!pending?.radiusKm || !point) return;
  const center = screenPointToLatLng(state, point);
  if (!center) return;
  const [firstPoint, lastPoint] = circleDiameterPoints(center, pending.radiusKm);
  const first = latLngToScreen(state, firstPoint.lat, firstPoint.lng);
  const last = latLngToScreen(state, lastPoint.lat, lastPoint.lng);
  const radius = Math.hypot(last.x - first.x, last.y - first.y) / 2;
  if (!Number.isFinite(radius)) return;
  const ctx = state.ctx;
  ctx.save();
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(37, 99, 235, 0.10)';
  ctx.fill();
  ctx.strokeStyle = '#2563eb';
  ctx.lineWidth = 2;
  ctx.setLineDash([6, 5]);
  ctx.stroke();
  ctx.setLineDash([]);
  const lineLength = Math.min(radius, state.mapElement.clientWidth - point.x - 24);
  ctx.beginPath();
  ctx.moveTo(point.x, point.y);
  ctx.lineTo(point.x + Math.max(0, lineLength), point.y);
  ctx.stroke();
  const value = pending.unit === 'mi' ? pending.radiusKm / 1.60934 : pending.radiusKm;
  const label = `${new Intl.NumberFormat(undefined, { maximumSignificantDigits: 6 }).format(value)} ${pending.unit}`;
  ctx.font = '600 13px system-ui, sans-serif';
  const width = ctx.measureText(label).width;
  const labelX = Math.min(point.x + Math.max(12, lineLength / 2), state.mapElement.clientWidth - width - 20);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(labelX - 6, point.y - 28, width + 12, 22);
  ctx.fillStyle = '#1e40af';
  ctx.fillText(label, labelX, point.y - 12);
  ctx.beginPath();
  ctx.arc(point.x, point.y, 6, 0, Math.PI * 2);
  ctx.fillStyle = '#2563eb';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.restore();
}
